export const WORKER = {
  fulltime: "正社員",
  freelancer: "個人事業主",
  part_time: "アルバイト",
  hourly_work: "パート",
};

export const BANK_TYPE_OPTIONS = [
  {
    value: "普通",
    label: "普通",
  },
  {
    value: "当座",
    label: "当座",
  },
];

export const BANK_TYPE_VALUES = BANK_TYPE_OPTIONS.map((i) => i?.value);

export const WORKER_FIELD = {
  // Basic fields
  WORKING_TYPE: "working_type",
  PREFECTURE_ID: "prefecture_id",
  ADDRESS_PREFECTURE_ID: "address_prefecture_id",
  EMERGENCY_PREFECTURE_ID: "emergency_prefecture_id",
  TEL: "tel",

  // Bank account fields
  SALARY_ACCOUNT_TYPE: "salary_account.account_type",
  PAYMENT_ACCOUNT_TYPE: "payment_account.account_type",

  // Date fields
  JOIN_DATE: "join_date",
  RETIRED_DATE: "retired_date",
  HEALTH_INSURANCE_GET_DATE: "health_insurance_get_date",
  HEALTH_INSURANCE_LOST_DATE: "health_insurance_lost_date",
  EMPLOYMENT_INSURANCE_GET_DATE: "employment_insurance_get_date",
  EMPLOYMENT_INSURANCE_LOST_DATE: "employment_insurance_lost_date",

  // Base salary
  WORKER_BASE_SALARY: "worker_base_salary.base_salary",
} as const;

/* eslint-disable no-unused-vars */
/**
 * Enum for Table Keys used throughout the application
 * Centralized table key management for consistency
 */
export enum TableKey {
  // User Information Tab (ユーザー情報)
  LOGIN_INFO = "login-info",
  NAME_HISTORY = "name-history",
  GENDER = "gender",
  CURRENT_ADDRESS = "current-address",
  RESIDENT_ADDRESS = "resident-address",
  WORKER_PHONE_NUMBERS_LANDLINE = "worker_phone_numbers_landline",
  WORKER_PHONE_NUMBERS_MOBILE = "worker_phone_numbers_mobile",
  WORKER_STORES = "worker_stores",
  WORKER_BANK_INFOS = "worker_bank_infos",
  EMERGENCY_BASIC = "emergency-basic",
  EMERGENCY_ADDRESS = "emergency-address",
  REGISTRATION_CODE = "registration-code",

  EDUCATION_WORK = "education_work",
  QUALIFICATIONS = "qualifications",
  JOB_DETAILS = "job_details",

  // Join Information Tab (入社情報)
  EMPLOYMENT = "employment",
  SOCIAL_SECURITY = "social_security",
  WORKER_DEVICES = "worker_devices",
  HEALTH_INSURANCE = "health-insurance",
  EMPLOYMENT_INSURANCE = "employment-insurance",
  WORKER_BASE_SALARIES = "worker_base_salaries",
  WORKER_COMPANY_EMAIL = "worker_company_email",
  WORKER_EMAILS = "worker_emails",
  WORKER_DOCUMENTS = "worker_documents",
  COMPANY_INFO = "company_info",

  // Tab 6
  WORKER_DEPENDANT_GENDERS = "worker_dependant_genders",
  WORKER_DEPENDANT_NAMES = "worker_dependant_names",
  WORKER_DEPENDANT_ADDRESSES = "worker_dependant_addresses",
  WORKER_DEPENDANT_RELATIONSHIPS = "worker_dependant_relationships",
}

/**
 * Type alias for backward compatibility
 * Use this when you need the string union type
 */
export type TableKeyType = `${TableKey}`;

/**
 * Helper function to get table key as string
 * @param key - TableKey enum value
 * @returns string representation of the table key
 */
export const getTableKeyString = (key: TableKey): string => {
  return key;
};

/**
 * Helper function to check if a string is a valid table key
 * @param key - string to check
 * @returns boolean indicating if the string is a valid table key
 */
export const isValidTableKey = (key: string): key is TableKeyType => {
  return Object.values(TableKey).includes(key as TableKey);
};

/**
 * Helper function to get table key from string
 * @param key - string representation of table key
 * @returns TableKey enum value or undefined if invalid
 */
export const getTableKeyFromString = (key: string): TableKey | undefined => {
  return isValidTableKey(key) ? (key as TableKey) : undefined;
};

/**
 * Table key categories for organization
 */
export const TableKeyCategories = {
  USER_INFO: [
    TableKey.LOGIN_INFO,
    TableKey.NAME_HISTORY,
    TableKey.GENDER,
    TableKey.CURRENT_ADDRESS,
    TableKey.RESIDENT_ADDRESS,
    TableKey.WORKER_PHONE_NUMBERS_LANDLINE,
    TableKey.WORKER_PHONE_NUMBERS_MOBILE,
    TableKey.WORKER_STORES,
    TableKey.WORKER_BANK_INFOS,
    TableKey.EMERGENCY_BASIC,
    TableKey.EMERGENCY_ADDRESS,
    TableKey.WORKER_EMAILS,
    TableKey.QUALIFICATIONS,
    TableKey.EDUCATION_WORK,
    TableKey.JOB_DETAILS,
  ],
  JOIN_INFO: [
    TableKey.EMPLOYMENT,
    TableKey.SOCIAL_SECURITY,
    TableKey.WORKER_DEVICES,
    TableKey.HEALTH_INSURANCE,
    TableKey.EMPLOYMENT_INSURANCE,
    TableKey.WORKER_BASE_SALARIES,
  ],
} as const;

/**
 * Helper function to get table keys by category
 * @param category - category name
 * @returns array of table keys in that category
 */
export const getTableKeysByCategory = (
  category: keyof typeof TableKeyCategories,
): readonly TableKey[] => {
  return TableKeyCategories[category];
};

/**
 * Helper function to get category for a table key
 * @param key - table key
 * @returns category name or undefined if not found
 */
export const getCategoryForTableKey = (
  key: TableKey,
): keyof typeof TableKeyCategories | undefined => {
  for (const [category, keys] of Object.entries(TableKeyCategories)) {
    if ((keys as readonly TableKey[]).includes(key)) {
      return category as keyof typeof TableKeyCategories;
    }
  }
  return undefined;
};
